import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import BoxSection from './BoxSection'
import { useCurrentWorkspaceDetailQuery } from '../../../hooks/queries'
import { customDayjs } from '../../../utils/date'
import { isAnnual } from '../../../services/payments/annual'
import { cancelScheduledChange } from '../../../services/payments'
import { workspaceQueries } from '../../../services/workspace'
import useDialog from '../../../hooks/useDialog'
import AivatarButton from '../../buttons/AivatarButton'

type Props = {
  className?: string
}

/**
 * 예약 전환 상태 표기와 취소 (기획 전달본 S4, 정책 11.6·11.8).
 *
 * 연→월 전환과 연간 등급 다운그레이드는 신청해도 지금 바뀌지 않는다. 신청 직후 화면이
 * 그대로면 사용자는 신청이 안 된 줄 알고 다시 누르므로 상시 노출한다.
 *
 * 취소는 BE aivatar #484 의 scheduled-change/cancel 이 맡는다. 해지 예약(PAUSE)은 이
 * 섹션이 아예 그리지 않으므로(아래 CHANGED 게이트) 그쪽으로 새어 나갈 일은 없다 —
 * 해지 예약의 되돌림은 continueSubscribe 가 따로 담당한다.
 */
export default function ScheduledChangeSection({ className }: Props) {
  const { data: workspace } = useCurrentWorkspaceDetailQuery()
  const { t } = useTranslation('subscription')
  const dialog = useDialog()
  const queryClient = useQueryClient()
  const { mutate: mutateToCancel, isPending: cancelling } = useMutation({
    mutationFn: cancelScheduledChange,
  })

  const next = workspace?.nextSubscription
  const current = workspace?.subscription
  if (next?.status !== 'CHANGED') return null

  const appliedAt = customDayjs(next.startedAt).format('YYYY-MM-DD')

  // 취소하면 현행 약정이 그대로 이어지므로 갱신도 현행 플랜·금액으로 돌아간다.
  // 만료일 다음 날이 갱신일이다 (BE current.endAt.plusDays(1), RenewalNoticeSection 과 같은 규칙).
  const renewAt = current?.endAt ? customDayjs(current.endAt).add(1, 'day').format('YYYY-MM-DD') : ''

  const cancelChange = () => {
    if (!workspace) return
    dialog.close()
    mutateToCancel(workspace.id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: workspaceQueries.detail(workspace.id).queryKey,
        })
      },
      onError: () => {
        dialog.alert({
          title: t('예약 취소 실패'),
          message: t('예약 취소에 실패했습니다. 관리자에게 문의바랍니다.'),
        })
      },
    })
  }

  // 정책 11.6 은 취소 확인 화면에서 "갱신 예정 요금제·금액이 바뀐다는 것"을 안내하도록 한다.
  // 예약을 걸면서 갱신 고지가 예약 반영 금액으로 이미 나갔을 수 있어, 취소 후 실제로 청구될
  // 금액을 여기서 다시 알려야 한다.
  const confirmCancel = () => {
    dialog.confirm({
      title: t('예약을 취소하시겠습니까?'),
      message: t(
        '취소하면 지금 플랜이 그대로 이어집니다.\n{{date}} 갱신은 {{plan_name}} {{interval}} {{amount}}원(부가세 포함)으로 진행됩니다.\n정산이나 환불은 없습니다.',
        {
          date: renewAt,
          plan_name: current?.plan?.displayName,
          interval: isAnnual(current?.intervalMonth) ? t('연간') : t('월간'),
          amount: Number(current?.price ?? 0).toLocaleString(),
        },
      ),
      confirmButtonName: t('예약 취소'),
      onClickConfirm: cancelChange,
      cancelButtonName: t('닫기', { ns: 'modal' }),
      onClickCancel: dialog.close,
    })
  }

  return (
    <BoxSection className={className}>
      <span className="bg-main-4 text-main-1 inline-block rounded-[500px] px-[12px] py-[3px] text-[12px] font-[700]">
        {t('예약된 변경')}
      </span>

      {/* 등급과 주기를 동시에 예약해도 적용일이 같으므로 한 줄로 합성한다 (D-31 · D-13) */}
      <p className="text-gray-2 mt-[12px] break-keep text-[14px] font-[700]">
        {t('{{date}}부터 {{plan_name}} {{interval}}으로 바뀝니다.', {
          date: appliedAt,
          plan_name: next.plan?.displayName,
          interval: isAnnual(next.intervalMonth) ? t('연간') : t('월간'),
        })}
      </p>
      <p className="text-gray-21 mt-[6px] break-keep text-[12px]">
        {t('그때까지는 지금 플랜을 그대로 이용하고, 중간 정산이나 환불은 없습니다.')}
      </p>

      <div className="mt-[16px] flex items-center gap-[10px]">
        <AivatarButton type="button" variant="m4" disabled={cancelling} onClick={confirmCancel}>
          {t('예약 취소')}
        </AivatarButton>
        <span className="text-gray-3 text-[12px]">{t('적용일 전까지 언제든 취소할 수 있습니다.')}</span>
      </div>
    </BoxSection>
  )
}
